import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';
import Input from '../../../../components/ui/Input';
import { SectionTitle } from './ChoiceControls';

export interface ExportEmployee {
  id: number;
  name: string;
  staffNumber: string;
  /** Empty when the employee has no department. */
  department: string;
}

interface Props {
  employees: ExportEmployee[];
  selected: Set<number>;
  onChange: (next: Set<number>) => void;
  /** Says where the starting set came from, so the ticks never look arbitrary. */
  startNote: string;
  onResetToStart?: () => void;
}

interface DepartmentBlock {
  department: string;
  rows: ExportEmployee[];
}

const EmployeePicker: React.FC<Props> = ({ employees, selected, onChange, startNote, onResetToStart }) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  const blocks = useMemo<DepartmentBlock[]>(() => {
    const byDepartment = new Map<string, ExportEmployee[]>();
    for (const employee of employees) {
      const haystack = `${employee.name} ${employee.staffNumber} ${employee.department}`.toLowerCase();
      if (query && !haystack.includes(query)) continue;
      const rows = byDepartment.get(employee.department);
      if (rows) rows.push(employee); else byDepartment.set(employee.department, [employee]);
    }
    const collator = new Intl.Collator(i18n.language);
    return Array.from(byDepartment, ([department, rows]) => ({
      department,
      rows: rows.sort((a, b) => collator.compare(a.name, b.name)),
    })).sort((a, b) => {
      // Employees with no department read as a leftover group, so they go last.
      if (!a.department || !b.department) return a.department ? -1 : b.department ? 1 : 0;
      return collator.compare(a.department, b.department);
    });
  }, [employees, query, i18n.language]);

  const shownIds = useMemo(() => blocks.flatMap((b) => b.rows.map((r) => r.id)), [blocks]);

  const setMany = (ids: number[], on: boolean) => {
    const next = new Set(selected);
    ids.forEach((id) => { if (on) next.add(id); else next.delete(id); });
    onChange(next);
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col" aria-labelledby="payroll-export-employees">
      <div className="space-y-3 p-5 pb-3">
        <div className="flex items-baseline justify-between gap-3">
          <SectionTitle id="payroll-export-employees">{t('payrollExport.section.employees')}</SectionTitle>
          <span className="text-sm tabular-nums text-muted-foreground" aria-live="polite" data-testid="payroll-export-count">
            {t('payrollExport.employees.count', { selected: selected.size, count: employees.length })}
          </span>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="payroll-export-start-note">
          {startNote}
          {onResetToStart && (
            <button type="button" onClick={onResetToStart} className="ms-1.5 rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
              {t('Reset')}
            </button>
          )}
        </p>
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            className="ps-9"
            aria-label={t('payrollExport.employees.search')}
            placeholder={t('payrollExport.employees.search')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            data-testid="payroll-export-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="xs" disabled={!shownIds.length} onClick={() => setMany(shownIds, true)} data-testid="payroll-export-select-all">
            {query ? t('payrollExport.employees.selectShown') : t('Select all')}
          </Button>
          <Button variant="ghost" size="xs" disabled={!shownIds.length} onClick={() => setMany(shownIds, false)} data-testid="payroll-export-clear">
            {query ? t('payrollExport.employees.clearShown') : t('Clear')}
          </Button>
        </div>
      </div>

      {/* `relative` is load-bearing: each Checkbox holds an sr-only input, and this bounded scroller must
          be the box that clips them (docs/claude/ui-standards.md, "Where relative goes"). */}
      <div className="relative max-h-80 min-h-0 flex-1 overflow-y-auto border-t border-border lg:max-h-none" data-testid="payroll-export-list">
        {!shownIds.length && (
          <p className="p-5 text-sm text-muted-foreground">{t('payrollExport.employees.noMatch')}</p>
        )}
        {blocks.map(({ department, rows }, blockIndex) => {
          const ids = rows.map((r) => r.id);
          const on = ids.filter((id) => selected.has(id)).length;
          const headingId = `payroll-export-dept-${blockIndex}`;
          return (
            <div key={department || 'none'} role="group" aria-labelledby={headingId}>
              <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-muted px-5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
                <Checkbox
                  id={`${headingId}-all`}
                  checked={on === ids.length}
                  indeterminate={on > 0 && on < ids.length}
                  onChange={() => setMany(ids, on !== ids.length)}
                />
                <label id={headingId} htmlFor={`${headingId}-all`} className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 py-1.5 text-xs font-medium text-foreground">
                  <span className="truncate" dir="auto">{department || t('payrollExport.workbook.noDepartment')}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground" dir="ltr">{on} / {ids.length}</span>
                </label>
              </div>
              <ul>
                {rows.map((row) => {
                  const id = `payroll-export-employee-${row.id}`;
                  return (
                    <li key={row.id} className="flex items-center gap-2 px-5 hover:bg-muted/60 focus-within:bg-muted/60 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
                      <Checkbox
                        id={id}
                        checked={selected.has(row.id)}
                        onChange={() => setMany([row.id], !selected.has(row.id))}
                      />
                      <label htmlFor={id} className="flex min-h-10 min-w-0 flex-1 cursor-pointer items-center justify-between gap-3 py-1.5 text-sm text-foreground">
                        <span className="truncate" dir="auto" title={row.name}>{row.name}</span>
                        {row.staffNumber && <span className="shrink-0 text-xs tabular-nums text-muted-foreground" dir="ltr">{row.staffNumber}</span>}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default EmployeePicker;
